import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';

export interface ChatAction {
  type: 'company' | 'theme' | 'page' | 'company_theme';
  label: string;
  entityid?: string;
  entity_name?: string;
  gsubind?: string;
  companytype?: string;
  query?: string;
  theme_name?: string;
  cluster_id?: string;
  cluster_name?: string;
  theme_search_type?: 'cluster' | 'custom';
  sideTab?: string;
  route?: string;
}

export interface ChatWidgetMessage {
  sender: 'user' | 'assistant';
  text: string | SafeHtml;
  actions?: ChatAction[];
}

@Injectable({ providedIn: 'root' })
export class ChatWidgetService {
  messages: ChatWidgetMessage[] = [];
  conversationId = '';
  isOpen = false;
  isLoading = false;
  isStreaming = false;
  hasUnread = false;

  private readonly WELCOME_TEXT =
    "Hi! I'm <strong>SIA Helper</strong>, your navigation guide. Ask me about a company, a technology theme, or how a company is performing in a specific technology — I'll find it and you can open it on the results page in one click. I can also explain SIA's features and metrics.";

  constructor(private sanitizer: DomSanitizer, private router: Router) {
    this.resetToWelcome();
  }

  private authHeader(): string | null {
    const token = localStorage.getItem('current_user_token');
    return token ? 'Bearer ' + token : null;
  }

  private formatResponse(text: string): string {
    // Escape raw HTML first — model output is untrusted and is rendered via bypassSecurityTrustHtml.
    // The markdown transforms below only add our own safe tags afterwards.
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // strip placeholder-style markdown links like [text](#) that the LLM sometimes emits alongside action buttons
      .replace(/\[([^\]]+)\]\(#\)/g, '$1')
      .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank">$1</a>')
      .replace(/(?<!href=")(https?:\/\/[^\s<]+[^.,;:!?\s<])(?!")/g, '<a href="$1" target="_blank">$1</a>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br>');
  }

  toggle() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) this.hasUnread = false;
  }

  startNewChat() {
    this.conversationId = '';
    this.resetToWelcome();
  }

  private resetToWelcome() {
    this.messages = [
      {
        sender: 'assistant',
        text: this.sanitizer.bypassSecurityTrustHtml(this.WELCOME_TEXT),
      },
    ];
  }

  async sendMessage(text: string) {
    if (!text.trim() || this.isLoading) return;

    this.messages.push({ sender: 'user', text });
    this.isLoading = true;

    const assistantMessage: ChatWidgetMessage = {
      sender: 'assistant',
      text: this.sanitizer.bypassSecurityTrustHtml(''),
    };
    let messagePushed = false;
    let buffer = '';

    const auth = this.authHeader();
    if (!auth) {
      assistantMessage.text =
        'Your session has expired. Please refresh the page and log in again.';
      this.messages.push(assistantMessage);
      this.isLoading = false;
      this.isStreaming = false;
      if (!this.isOpen) this.hasUnread = true;
      return;
    }

    try {
      const response = await fetch(
        `/backend/navAssistant/assistant?conversation_id=${this.conversationId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: auth,
          },
          body: JSON.stringify({
            user_message: text,
            model: 'gpt-4.1',
            current_route: this.router.url,
          }),
        }
      );

      if (response.status === 401 || response.status === 403) {
        throw new Error('auth');
      }
      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });

        // SSE events are separated by blank lines
        let sepIdx: number;
        while ((sepIdx = sseBuffer.indexOf('\n\n')) >= 0) {
          const raw = sseBuffer.slice(0, sepIdx);
          sseBuffer = sseBuffer.slice(sepIdx + 2);

          // Each event may be multiple lines; we only care about lines starting with "data:"
          const dataLines = raw
            .split('\n')
            .filter((l) => l.startsWith('data:'))
            .map((l) => l.slice(5).trimStart());
          if (!dataLines.length) continue;

          let event: any;
          try {
            event = JSON.parse(dataLines.join('\n'));
          } catch {
            continue;
          }

          if (event.type === 'token') {
            buffer += event.text || '';
            this.isStreaming = true;
            assistantMessage.text = this.sanitizer.bypassSecurityTrustHtml(
              this.formatResponse(buffer)
            );
            if (!messagePushed) {
              this.messages.push(assistantMessage);
              messagePushed = true;
            }
          } else if (event.type === 'actions') {
            const actions = this.extractActions(event.entities, event.themes, event.pages, event.company_themes);
            if (actions.length) {
              assistantMessage.actions = actions;
              if (!messagePushed) {
                this.messages.push(assistantMessage);
                messagePushed = true;
              }
            }
          } else if (event.type === 'done') {
            if (event.conversation_id) this.conversationId = event.conversation_id;
          } else if (event.type === 'error') {
            throw new Error(event.message || 'stream error');
          }
        }
      }

      if (!buffer && !assistantMessage.actions) {
        assistantMessage.text = this.sanitizer.bypassSecurityTrustHtml('No response.');
      }
      if (!messagePushed) {
        this.messages.push(assistantMessage);
        messagePushed = true;
      }
      if (!this.isOpen) this.hasUnread = true;
    } catch (err: any) {
      console.error('[chat-widget] sendMessage failed:', err);
      assistantMessage.text =
        err?.message === 'auth'
          ? 'Your session has expired. Please refresh the page and log in again.'
          : 'Sorry, something went wrong. Please try again.';
      assistantMessage.actions = undefined;
      if (!messagePushed) {
        this.messages.push(assistantMessage);
        messagePushed = true;
      }
    } finally {
      this.isLoading = false;
      this.isStreaming = false;
    }
  }

  private extractActions(entities?: any[], themes?: any[], pages?: any[], companyThemes?: any[]): ChatAction[] {
    const actions: ChatAction[] = [];
    const seenIds = new Set<string>();

    if (companyThemes) {
      const seenCT = new Set<string>();
      for (const ct of companyThemes) {
        if (!ct.entityid || !ct.cluster_id) continue;
        const key = `${ct.entityid}|${ct.cluster_id}`;
        if (seenCT.has(key)) continue;
        seenCT.add(key);
        actions.push({
          type: 'company_theme',
          label: ct.label || `View ${ct.entity_name || 'company'} in ${ct.cluster_name || 'theme'}`,
          entityid: ct.entityid,
          entity_name: ct.entity_name,
          cluster_id: ct.cluster_id,
          cluster_name: ct.cluster_name,
        });
      }
    }

    if (entities) {
      for (const e of entities) {
        if (e.entityid && !seenIds.has(e.entityid + (e.side_tab || ''))) {
          seenIds.add(e.entityid + (e.side_tab || ''));
          actions.push({
            type: 'company',
            label: e.label || `View ${e.entity_name || e.entityid}`,
            entityid: e.entityid,
            entity_name: e.entity_name,
            gsubind: e.gsubind,
            companytype: e.companytype,
            sideTab: e.side_tab,
          });
        }
      }
    }

    if (themes) {
      const seenKeys = new Set<string>();
      for (const t of themes) {
        const idPart = t.cluster_id ? `c:${t.cluster_id}` : t.query ? `q:${t.query}` : '';
        if (!idPart) continue;
        const key = idPart + '|' + (t.side_tab || '');
        if (seenKeys.has(key)) continue;
        seenKeys.add(key);
        actions.push({
          type: 'theme',
          label: t.label || `Explore ${t.name || 'theme'}`,
          query: t.query,
          theme_name: t.name,
          cluster_id: t.cluster_id,
          theme_search_type: t.theme_search_type,
          sideTab: t.side_tab,
        });
      }
    }

    if (pages) {
      for (const p of pages) {
        if (p.route) {
          actions.push({
            type: 'page',
            label: p.label || 'Navigate',
            route: p.route,
          });
        }
      }
    }

    return actions;
  }
}
